import React, { useState, useEffect } from "react";
import {
    Box, FormControl, Select, InputLabel, MenuItem, Typography, Table, TableCell, TableContainer, TableHead, TableRow,
    Paper, Tooltip, TextField, IconButton, InputAdornment, Button, Checkbox, TableBody, useMediaQuery, useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TurnedInIcon from "@mui/icons-material/TurnedIn";
import CancelIcon from "@mui/icons-material/Cancel";
import axiosInstance from "../../../lib/axios";

const columns = [
    { id: "sno", minWidth: { xs: 30, sm: 50 }, align: "center", editable: false },
    { id: "category", minWidth: { xs: 80, sm: 100 }, align: "left", editable: false },
    { id: "article", minWidth: { xs: 80, sm: 100 }, align: "left", editable: false },
    { id: "standard", minWidth: { xs: 70, sm: 80 }, align: "left", editable: true },
    { id: "damage", minWidth: { xs: 70, sm: 100 }, align: "left", editable: true },
    { id: "actions", minWidth: { xs: 70, sm: 100 }, align: "center" },
];

const ChkinArtConfig = () => {
    const { register, watch, setValue } = useForm();
    const [rows, setRows] = useState([]);
    const [propertyDrp, setPropertyDrp] = useState([]);
    const [roomTypeDrp, setRoomTypeDrp] = useState([]);
    const [bedTypeDrp, setBedTypeDrp] = useState([]);
    const [categoryDrp, setCategoryDrp] = useState([]);
    const [articleDrp, setArticleDrp] = useState([]);
    const [selectedArticles, setSelectedArticles] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [editingRow, setEditingRow] = useState(null);
    const [editedValues, setEditedValues] = useState({});
    const [userId] = useState(sessionStorage.getItem("UserId"));
    const navigate = useNavigate();
    const location = useLocation();
    const { ArtConfigId, PropId, RoomTypeId, RoomBedTypeId, mode } = location.state || {};

    // Add useTheme and useMediaQuery to detect mobile
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Mobile is xs, sm and below

    useEffect(() => {
        fetchPropertyData();
        fetchCategoryData();
    }, []);

    useEffect(() => {
        const propId = watch("PropId");
        const roomTypeId = watch("RoomTypeId");
        const bedTypeId = watch("BedTypeId");
        if (propId && roomTypeId && bedTypeId) {
            fetchArticleConfigData();
        }
    }, [watch("PropId"), watch("RoomTypeId"), watch("BedTypeId")]);

    const fetchPropertyData = async () => {
        try {
            const response = await axiosInstance.post(`PropertyMst/GetPropertyMstUserwisedrp`, {
                Flag: "",
                userId,
            });
            if (response.data.Status === 0 && response.data.Data?.length > 0) {
                setPropertyDrp(response.data.Data);
                if (!watch("PropId") && PropId) {
                    setValue("PropId", PropId);
                    fetchRoomTypeData(PropId);
                    fetchBedTypeData(PropId);
                } else if (!watch("PropId") && response.data.Data[0]?.Id) {
                    setValue("PropId", response.data.Data[0].Id);
                    fetchRoomTypeData(response.data.Data[0].Id);
                    fetchBedTypeData(response.data.Data[0].Id);
                }
            } else {
                toast.error("Error fetching property data");
            }
        } catch (error) {
            toast.error("Error fetching property data");
        }
    };

    const fetchRoomTypeData = async (propId) => {
        if (!propId) {
            toast.error("No property ID provided for room type fetch");
            setRoomTypeDrp([]);
            return;
        }
        try {
            const response = await axiosInstance.post(`RoomTypeMst/getdrpRoomTypeMst`, {
                PropId: propId,
                Flag: "",
            });
            if (response.data.Status === 0 && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                const mappedData = response.data.Data
                    .filter((item) => item.Name && item.Name.trim() !== "")
                    .map((item) => ({
                        Id: item.Id,
                        Name: item.Name,
                    }));
                setRoomTypeDrp(mappedData);
                if (!watch("RoomTypeId") && RoomTypeId) {
                    setValue("RoomTypeId", RoomTypeId);
                } else if (!watch("RoomTypeId") && mappedData[0]?.Id) {
                    setValue("RoomTypeId", mappedData[0].Id);
                }
            } else {
                toast.error("No room types found or invalid response structure");
                setRoomTypeDrp([]);
            }
        } catch (error) {
            console.error("Error fetching room type data:", error);
            toast.error("Error fetching room type data: " + error.message);
            setRoomTypeDrp([]);
        }
    };

    const fetchBedTypeData = async (propId) => {
        if (!propId) {
            toast.error("No property ID provided for bed type fetch");
            setBedTypeDrp([]);
            return;
        }
        try {
            const response = await axiosInstance.post(`RoomBedTypeMst/getdrpBedTypeMst`, {
                PropId: propId,
                Flag: "",
            });
            if (response.data.Status === 0) {
                setBedTypeDrp(response.data.Data || []);
                if (!watch("BedTypeId") && RoomBedTypeId) {
                    setValue("BedTypeId", RoomBedTypeId);
                } else if (!watch("BedTypeId") && response.data.Data[0]?.Id) {
                    setValue("BedTypeId", response.data.Data[0].Id);
                }
            } else {
                toast.error("Error fetching bed type data");
                setBedTypeDrp([]);
            }
        } catch (error) {
            toast.error("Error fetching bed type data");
            setBedTypeDrp([]);
        }
    };

    const fetchCategoryData = async () => {
        try {
            const response = await axiosInstance.post(`MstChkArtCat/getMstChkArtCatdrp`, {});
            if (response.data.Status === 0) {
                setCategoryDrp(response.data.Data || []);
            } else {
                toast.error("Error fetching category data");
                setCategoryDrp([]);
            }
        } catch (error) {
            toast.error("Error fetching category data");
            setCategoryDrp([]);
        }
    };

    const fetchArticleDropdownData = async (categoryId) => {
        try {
            const response = await axiosInstance.post(`MstChkArticle/getMstChkArticledrp`, {
                ChkArtCatId: categoryId,
                Flag: "",
            });
            if (response.data.Status === 0) {
                setArticleDrp(response.data.Data || []);
            } else {
                toast.error("Error fetching article dropdown data");
                setArticleDrp([]);
            }
        } catch (error) {
            toast.error("Error fetching article dropdown data");
            setArticleDrp([]);
        }
    };

    const fetchArticleConfigData = async () => {
        const propId = watch("PropId");
        const roomTypeId = watch("RoomTypeId");
        const bedTypeId = watch("BedTypeId");
        if (!propId || !roomTypeId || !bedTypeId) {
            toast.error("Please select Property, Room Type, and Bed Type");
            return;
        }
        try {
            const response = await axiosInstance.post(`MstChkArtConfig/RetriveMstChkArtConfig`, {
                PropId: parseInt(propId),
                RoomTypeId: parseInt(roomTypeId),
                RoomBedTypeId: parseInt(bedTypeId),
                Flag: "",
            });
            if (response.data.Status === 0 && response.data.Data?.length > 0) {
                const formattedRows = response.data.Data.map((item, index) => ({
                    id: index + 1,
                    category: item.ChkArtCatName || "",
                    article: item.ChkArticleName || "",
                    code: item.Abrv || "",
                    standard: item.StdRate ? item.StdRate.toFixed(2) : "0.00",
                    damage: item.DmzChrg ? item.DmzChrg.toFixed(2) : "0.00",
                    ChkArticleId: item.ChkArticleId,
                    ChkArtCatId: item.ChkArtCatId,
                    ArtConfigDtlId: item.ArtConfigDtlId,
                }));
                const categoryIds = [...new Set(response.data.Data.map((item) => item.ChkArtCatId))];
                const selectedCategoryId = categoryIds[0] || "";
                setValue("CategoryId", selectedCategoryId);
                if (selectedCategoryId) {
                    await fetchArticleDropdownData(selectedCategoryId);
                }
                setRows(formattedRows);
                setSelectedArticles([]);
            } else {
                setRows([]);
                setSelectedArticles([]);
            }
        } catch (error) {
            toast.error("Error fetching article config data");
            setRows([]);
            setSelectedArticles([]);
        }
    };

    const handlePropertyChange = async (e) => {
        const propId = e.target.value;
        setValue("PropId", propId);
        setValue("RoomTypeId", "");
        setValue("BedTypeId", "");
        setRoomTypeDrp([]);
        setBedTypeDrp([]);
        setRows([]);
        setSearchQuery("");
        setEditingRow(null);
        setEditedValues({});
        if (propId) {
            await fetchRoomTypeData(propId);
            await fetchBedTypeData(propId);
        }
    };

    const handleRoomTypeChange = async (e) => {
        const roomTypeId = e.target.value;
        setValue("RoomTypeId", roomTypeId);
        setRows([]);
        setSearchQuery("");
        setEditingRow(null);
        setEditedValues({});
        const propId = watch("PropId");
        const bedTypeId = watch("BedTypeId");
        if (propId && roomTypeId && bedTypeId) {
            await fetchArticleConfigData();
        }
    };

    const handleBedTypeChange = async (e) => {
        const bedTypeId = e.target.value;
        setValue("BedTypeId", bedTypeId);
        setRows([]);
        setSearchQuery("");
        setEditingRow(null);
        setEditedValues({});
        const propId = watch("PropId");
        const roomTypeId = watch("RoomTypeId");
        if (propId && roomTypeId && bedTypeId) {
            await fetchArticleConfigData();
        }
    };

    const handleCategoryChange = async (e) => {
        const categoryId = e.target.value;
        setValue("CategoryId", categoryId);
        setArticleDrp([]);
        setSelectedArticles([]);
        if (categoryId) {
            await fetchArticleDropdownData(categoryId);
        }
    };

    const handleArticleToggle = (articleId) => {
        setSelectedArticles((prev) =>
            prev.includes(articleId) ? prev.filter((id) => id !== articleId) : [...prev, articleId]
        );
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleEditClick = (row, idx) => {
        setEditingRow(idx);
        setEditedValues({
            id: row.id,
            standard: row.standard,
            damage: row.damage,
        });
    };

    const handleDeleteClick = async (idx) => {
        const row = rows[idx];
        const deletedId = row.ChkArticleId;
        const artConfigDtlId = row.ArtConfigDtlId;
        if (artConfigDtlId) {
            try {
                const response = await axiosInstance.post(`MstChkArtConfig/DeleteMstChkArtConfigDtl`, {
                    ArtConfigId: parseInt(ArtConfigId || 1),
                    ArtConfigDtlId: parseInt(artConfigDtlId),
                    Flag: "",
                });
                if (response.data.Status === 0) {
                    const updatedRows = rows.filter((_, index) => index !== idx);
                    setRows(updatedRows);
                    setSelectedArticles((prev) => prev.filter((id) => id !== deletedId));
                    toast.success(response.data.Message);
                } else {
                    toast.error(response.data.Message);
                }
            } catch (error) {
                console.error("Error deleting row from server:", error);
            }
        } else {
            const updatedRows = rows.filter((_, index) => index !== idx);
            setRows(updatedRows);
            setSelectedArticles((prev) => prev.filter((id) => id !== deletedId));
            toast.success("Row deleted locally");
        }
    };

    const handleCancel = () => {
        setEditingRow(null);
        setEditedValues({});
    };

    const handleSaveClick = async (row, idx) => {
        const updatedRows = [...rows];
        updatedRows[idx] = { ...row, ...editedValues };
        setRows(updatedRows);
        setEditingRow(null);
        setEditedValues({});
    };

    const handleCancelSelectedArticles = () => {
        if (selectedArticles.length > 0) {
            setSelectedArticles([]);
        }
    };

    const handleCancelNewRows = () => {
        if (editingRow !== null) {
            toast.error("Please save or cancel the row being edited before cancelling");
            return;
        }
        const newRows = rows.filter((row) => row.ArtConfigDtlId === null);
        if (newRows.length > 0) {
            const existingRows = rows.filter((row) => row.ArtConfigDtlId !== null);
            setRows(existingRows);
            setSelectedArticles([]);
        }
    };

    const handleConfirm = () => {
        if (selectedArticles.length === 0) {
            toast.error("Please select at least one article");
            return;
        }
        const existingArticleIds = rows.map((row) => row.ChkArticleId);
        const duplicateArticles = selectedArticles.filter((articleId) => existingArticleIds.includes(articleId));
        if (duplicateArticles.length > 0) {
            const duplicateArticleNames = articleDrp
                .filter((article) => duplicateArticles.includes(article.Id))
                .map((article) => article.Name)
                .join(", ");
            toast.error(`Cannot add already selected articles: ${duplicateArticleNames}`);
            return;
        }
        const selectedArticleData = articleDrp
            .filter((article) => selectedArticles.includes(article.Id))
            .map((article, index) => ({
                id: rows.length + index + 1,
                category: article.ChkArtCatName || "",
                article: article.Name || "",
                code: article.Abrv || "",
                standard: article.StdRate ? article.StdRate.toFixed(2) : "0.00",
                damage: article.DmzChrg ? article.DmzChrg.toFixed(2) : "0.00",
                ChkArticleId: article.Id,
                ChkArtCatId: article.ChkArtCatId,
                ArtConfigDtlId: null,
            }));
        setRows([...rows, ...selectedArticleData]);
        setSelectedArticles([]);
    };

    const handleSubmit = async () => {
        if (selectedArticles.length > 0) {
            return;
        }
        if (editingRow !== null) {
            toast.error("Please save the row data before submitting");
            return;
        }
        const propId = watch("PropId");
        const roomTypeId = watch("RoomTypeId");
        const bedTypeId = watch("BedTypeId");
        const categoryId = watch("CategoryId");
        if (!propId || !roomTypeId || !bedTypeId || !categoryId || rows.length === 0) {
            toast.error("Please fill in all required fields and select at least one article");
            return;
        }
        const isUpdateMode = !!ArtConfigId;
        const dbFlag = isUpdateMode ? "U" : "I";
        const artConfigId = isUpdateMode ? ArtConfigId : 0;
        const payload = [
            {
                PropId: parseInt(propId),
                RoomTypeId: parseInt(roomTypeId),
                RoomBedTypeId: parseInt(bedTypeId),
                Remark: "",
                Status: "1",
                CreatedBy: parseInt(userId),
                DBFLAG: dbFlag,
                ArtConfigId: artConfigId,
                MangeChkArtConfigDtl: rows.map((row, index) => {
                    const isRowUpdate = !!row.ArtConfigDtlId;
                    return {
                        DBFLAG: isRowUpdate ? "U" : "I",
                        ...(isRowUpdate ? { ArtConfigDtlId: row.ArtConfigDtlId } : {}),
                        ArtConfigId: isUpdateMode ? artConfigId : index + 1,
                        ChkArticleId: row.ChkArticleId,
                        ChkArtCatId: parseInt(row.ChkArtCatId),
                        StdRate: parseFloat(row.standard),
                        DmzChrg: parseFloat(row.damage),
                        Remark: "",
                        Status: "1",
                    };
                }),
            },
        ];
        try {
            const response = await axiosInstance.post(`MstChkArtConfig/ManageChkArtConfig`, payload);
            if (response.data.Status === 0) {
                toast.success("Data submitted successfully");
                fetchArticleConfigData();
            } else {
                toast.error("Error submitting data: " + response.data.Message || "Unknown error");
            }
        } catch (error) {
            toast.error("Error submitting data: " + error.message);
        }
    };

    const filteredRows = rows.filter(
        (row) =>
            row.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
            row.article.toLowerCase().includes(searchQuery.toLowerCase()) ||
            row.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
            row.standard.toLowerCase().includes(searchQuery.toLowerCase()) ||
            row.damage.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleBackClick = () => {
        navigate("/chkin-article-config-table");
    };

    const handleInputChange = (e, columnId) => {
        setEditedValues((prev) => ({
            ...prev,
            [columnId]: e.target.value,
        }));
    };

    const existingArticleIds = rows.map((row) => row.ChkArticleId);

    return (
        <Box
            sx={{
                width: { xs: "calc(100vw - 70px)", sm: "100%", md: "100%" },
                maxWidth: { xs: "calc(100vw - 70px)", sm: "100%", md: "100%" },
                overflowX: "hidden",
                overflowY: { xs: "auto", sm: "hidden", md: "hidden" },
                backgroundColor: "#f9f9f9",
                minHeight: "100vh",
                px: { xs: 1, sm: 2, md: 3 },
                boxSizing: "border-box",
            }}
        >
            <ToastContainer sx={{ maxWidth: "100%", overflowX: "hidden" }} />
            <Paper
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "hidden",
                    overflowY: { xs: "auto", sm: "hidden", md: "hidden" },
                    margin: { xs: "8px 0", sm: "10px 0" },
                    borderRadius: "12px",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    padding: { xs: "10px", sm: "12px", md: "15px" },
                    boxSizing: "border-box",
                    minHeight: { xs: "auto", md: "80vh" },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "hidden",
                        overflowY: { xs: "auto", sm: "hidden", md: "hidden" },
                        backgroundColor: "#f9f9f9",
                        boxSizing: "border-box",
                    }}
                >
                    <Paper
                        sx={{
                            width: "100%",
                            maxWidth: { xs: "100%", sm: "95%", md: "80%", lg: "70%" },
                            overflowX: "hidden",
                            overflowY: { xs: "auto", sm: "hidden", md: "hidden" },
                            margin: "0 auto",
                            border: "1px solid #e0e0e0",
                            padding: { xs: "10px", sm: "15px", md: "20px" },
                            boxSizing: "border-box",
                            minHeight: { xs: "auto", md: "50vh" },
                            borderRadius: "8px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: { xs: 2, sm: 3 },
                                maxWidth: "100%",
                                overflowX: "hidden",
                                overflowY: { xs: "auto", sm: "hidden", md: "hidden" },
                                gap: isMobile ? 0.5 : 0,
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: { xs: "20px", sm: "20px", md: "22px" },
                                    background: "linear-gradient(90deg, #9655d6ff 0%, #2455aaff 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
                                    fontFamily: `'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Check in Article Configuration
                            </Typography>
                            <Tooltip title="Previous Page" arrow>
                                <IconButton
                                    onClick={handleBackClick}
                                    sx={{
                                        backgroundColor: "#635bff",
                                        borderRadius: "50%",
                                        width: { xs: 28, sm: 32 },
                                        height: { xs: 28, sm: 32 },
                                        "&:hover": { backgroundColor: "#7b6eff" },
                                        boxShadow: 3,
                                    }}
                                >
                                    <ArrowBackIcon sx={{ color: "white", fontSize: { xs: "20px", sm: "20px" } }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                gap: { xs: 1.5, sm: 2 },
                                mb: { xs: 1.5, sm: 1 },
                                maxWidth: "100%",
                                overflowX: "hidden",
                            }}
                        >
                            <FormControl
                                variant="filled"
                                sx={{
                                    flex: 1,
                                    minWidth: { xs: "100%", sm: "200px" },
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                }}
                                className="custom-select"
                            >
                                <InputLabel id="property-select-label">
                                    <span>
                                        Property <span style={{ color: "red" }}>*</span>
                                    </span>
                                </InputLabel>
                                <Select
                                    labelId="property-select-label"
                                    id="PropId"
                                    name="PropId"
                                    value={watch("PropId") || ""}
                                    className="custom-textfield"
                                    {...register("PropId")}
                                    onChange={handlePropertyChange}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            },
                                        },
                                    }}
                                    sx={{
                                        backgroundColor: "#fff",
                                        borderRadius: "4px",
                                        fontSize: { xs: "16px", sm: "16px" },
                                        maxWidth: "100%",
                                        overflowX: "hidden",
                                        "& .MuiSelect-select": {
                                            padding: { xs: "10px 14px", sm: "10px 14px" },
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: { xs: "16px", sm: "14px" },
                                            top: { xs: "-4px", sm: "-4px" },
                                            "&.Mui-focused, &.MuiFormLabel-filled": {
                                                transform: "translate(12px, -6px) scale(0.75)",
                                            },
                                        },
                                    }}
                                >
                                    {propertyDrp.length > 0 ? (
                                        propertyDrp.map((type) => (
                                            <MenuItem key={type.Id} value={type.Id} sx={{ fontSize: { xs: "16px", sm: "14px" } }}>
                                                {type.Name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled sx={{ fontSize: { xs: "16px", sm: "14px" } }}>No properties available</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: { xs: 1.5, sm: 2 },
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                }}
                            >
                                <FormControl
                                    variant="filled"
                                    sx={{
                                        flex: 1,
                                        minWidth: { xs: "100%", sm: "150px" },
                                        maxWidth: "100%",
                                        overflowX: "hidden",
                                    }}
                                    className="custom-select"
                                >
                                    <InputLabel id="roomtype-select-label">
                                        <span>
                                            Room Type <span style={{ color: "red" }}>*</span>
                                        </span>
                                    </InputLabel>
                                    <Select
                                        labelId="roomtype-select-label"
                                        id="RoomTypeId"
                                        name="RoomTypeId"
                                        value={watch("RoomTypeId") || ""}
                                        className="custom-textfield"
                                        {...register("RoomTypeId")}
                                        onChange={handleRoomTypeChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200,
                                                    overflowX: "hidden",
                                                    overflowY: "auto",
                                                },
                                            },
                                        }}
                                        sx={{
                                            backgroundColor: "#fff",
                                            borderRadius: "4px",
                                            fontSize: { xs: "16px", sm: "16px" },
                                            maxWidth: "100%",
                                            overflowX: "hidden",
                                            "& .MuiSelect-select": {
                                                padding: { xs: "10px 14px", sm: "10px 14px" },
                                            },
                                            "& .MuiInputLabel-root": {
                                                fontSize: { xs: "16px", sm: "14px" },
                                                top: { xs: "-6px", sm: "-4px" },
                                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                                    transform: "translate(12px, -6px) scale(0.75)",
                                                },
                                            },
                                        }}
                                    >
                                        {roomTypeDrp.length > 0 ? (
                                            roomTypeDrp.map((type) => (
                                                <MenuItem key={type.Id} value={type.Id} sx={{ fontSize: { xs: "16px", sm: "14px" } }}>
                                                    {type.Name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled sx={{ fontSize: { xs: "16px", sm: "14px" } }}>No room types available</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    variant="filled"
                                    sx={{
                                        flex: 1,
                                        minWidth: { xs: "100%", sm: "150px" },
                                        maxWidth: "100%",
                                        overflowX: "hidden",
                                    }}
                                    className="custom-select"
                                >
                                    <InputLabel id="bedtype-select-label">
                                        <span>
                                            Bed Type <span style={{ color: "red" }}>*</span>
                                        </span>
                                    </InputLabel>
                                    <Select
                                        labelId="bedtype-select-label"
                                        id="BedTypeId"
                                        name="BedTypeId"
                                        value={watch("BedTypeId") || ""}
                                        className="custom-textfield"
                                        {...register("BedTypeId")}
                                        onChange={handleBedTypeChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200,
                                                    overflowX: "hidden",
                                                    overflowY: "auto",
                                                },
                                            },
                                        }}
                                        sx={{
                                            backgroundColor: "#fff",
                                            borderRadius: "4px",
                                            fontSize: { xs: "16px", sm: "16px" },
                                            maxWidth: "100%",
                                            overflowX: "hidden",
                                            "& .MuiSelect-select": {
                                                padding: { xs: "10px 14px", sm: "10px 14px" },
                                            },
                                            "& .MuiInputLabel-root": {
                                                fontSize: { xs: "16px", sm: "14px" },
                                                top: { xs: "-6px", sm: "-4px" },
                                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                                    transform: "translate(12px, -6px) scale(0.75)",
                                                },
                                            },
                                        }}
                                    >
                                        {bedTypeDrp.length > 0 ? (
                                            bedTypeDrp.map((type) => (
                                                <MenuItem key={type.Id} value={type.Id} sx={{ fontSize: { xs: "16px", sm: "14px" } }}>
                                                    {type.Name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled sx={{ fontSize: { xs: "16px", sm: "14px" } }}>No bed types available</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                gap: { xs: 1.5, sm: 2 },
                                mb: { xs: 1.5, sm: 1 },
                                maxWidth: "100%",
                                overflowX: "hidden",
                            }}
                        >
                            <FormControl
                                variant="filled"
                                sx={{
                                    flex: 1,
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                }}
                                className="custom-select"
                            >
                                <InputLabel id="category-select-label">
                                    <span>
                                        Check-in Category <span style={{ color: "red" }}>*</span>
                                    </span>
                                </InputLabel>
                                <Select
                                    labelId="category-select-label"
                                    id="CategoryId"
                                    name="CategoryId"
                                    value={watch("CategoryId") || ""}
                                    className="custom-textfield"
                                    {...register("CategoryId")}
                                    onChange={handleCategoryChange}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                                overflowX: "hidden",
                                                overflowY: "auto",
                                            },
                                        },
                                    }}
                                    sx={{
                                        backgroundColor: "#fff",
                                        borderRadius: "4px",
                                        fontSize: { xs: "16px", sm: "16px" },
                                        maxWidth: "100%",
                                        overflowX: "hidden",
                                        "& .MuiSelect-select": {
                                            padding: { xs: "10px 14px", sm: "10px 14px" },
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: { xs: "16px", sm: "14px" },
                                            top: { xs: "-4px", sm: "-4px" },
                                            "&.Mui-focused, &.MuiFormLabel-filled": {
                                                transform: "translate(12px, -6px) scale(0.75)",
                                            },
                                        },
                                    }}
                                >
                                    {categoryDrp.length > 0 ? (
                                        categoryDrp.map((category) => (
                                            <MenuItem key={category.Id} value={category.Id} sx={{ fontSize: { xs: "16px", sm: "14px" } }}>
                                                {category.Name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled sx={{ fontSize: { xs: "16px", sm: "14px" } }}>No categories available</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: 1, sm: 2 },
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                }}
                            >
                                <FormControl
                                    variant="filled"
                                    sx={{
                                        flex: 1,
                                        maxWidth: "100%",
                                        overflowX: "hidden",
                                    }}
                                    className="custom-select"
                                >
                                    <InputLabel id="article-select-label">
                                        <span>
                                            Articles <span style={{ color: "red" }}>*</span>
                                        </span>
                                    </InputLabel>
                                    <Select
                                        labelId="article-select-label"
                                        id="ArticleId"
                                        name="ArticleId"
                                        multiple
                                        value={selectedArticles}
                                        renderValue={(selected) =>
                                            selected.length > 0 ? (
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: "16px", sm: "16px" },
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        paddingTop: { xs: "10px", sm: "10px" },
                                                    }}
                                                >
                                                    {selected.length} article{selected.length !== 1 ? "s" : ""} selected
                                                </Typography>
                                            ) : null
                                        }
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200,
                                                    overflowX: "hidden",
                                                    overflowY: "auto",
                                                },
                                            },
                                        }}
                                        sx={{
                                            backgroundColor: "#fff",
                                            borderRadius: "4px",
                                            height: { xs: "40px", sm: "40px" },
                                            maxWidth: "100%",
                                            overflowX: "hidden",
                                            "& .MuiSelect-select": {
                                                padding: { xs: "10px 14px 2px", sm: "12px 14px 2px" },
                                                display: "flex",
                                                alignItems: "center",
                                                overflow: "hidden",
                                            },
                                            "& .MuiInputLabel-root": {
                                                fontSize: { xs: "16px", sm: "14px" },
                                                top: { xs: "-8px", sm: "-6px" },
                                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                                    transform: "translate(12px, -8px) scale(0.75)",
                                                },
                                            },
                                        }}
                                    >
                                        {articleDrp.length > 0 ? (
                                            articleDrp.map((article) => (
                                                <Tooltip
                                                    key={article.Id}
                                                    title={existingArticleIds.includes(article.Id) ? "Already added" : ""}
                                                    arrow
                                                >
                                                    <MenuItem
                                                        value={article.Id}
                                                        disabled={existingArticleIds.includes(article.Id)}
                                                        sx={{
                                                            padding: { xs: "2px 10px", sm: "0px 12px" },
                                                            minHeight: "32px",
                                                            "& .MuiCheckbox-root": {
                                                                padding: { xs: "4px", sm: "3px" },
                                                            },
                                                            "& .MuiTypography-root": {
                                                                fontSize: { xs: "16px", sm: "14px" },
                                                            },
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={selectedArticles.includes(article.Id)}
                                                            onChange={() => handleArticleToggle(article.Id)}
                                                            size="small"
                                                            sx={{
                                                                padding: { xs: "4px", sm: "3px" },
                                                                "& .MuiSvgIcon-root": {
                                                                    fontSize: { xs: "18px", sm: "16px" },
                                                                },
                                                            }}
                                                        />
                                                        {article.Name}
                                                    </MenuItem>
                                                </Tooltip>
                                            ))
                                        ) : (
                                            <MenuItem disabled sx={{ fontSize: { xs: "16px", sm: "14px" } }}>No articles available</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                                {isMobile && (
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Tooltip title="Save" arrow>
                                            <IconButton
                                                onClick={handleConfirm}
                                                size="small"
                                                sx={{
                                                    padding: { xs: "4px", sm: "4px" },
                                                    "& svg": { fontSize: { xs: "18px", sm: "20px" } },
                                                }}
                                            >
                                                <TurnedInIcon sx={{ color: "#23740eff" }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel" arrow>
                                            <IconButton
                                                onClick={handleCancelSelectedArticles}
                                                disabled={selectedArticles.length === 0}
                                                size="small"
                                                sx={{
                                                    padding: { xs: "4px", sm: "4px" },
                                                    "& svg": { fontSize: { xs: "18px", sm: "20px" } },
                                                }}
                                            >
                                                <CancelIcon sx={{ color: "#d21919ff" }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        {!isMobile && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    gap: 1,
                                    mb: { xs: 1.5, sm: 1 },
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleConfirm}
                                    sx={{
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        fontSize: { xs: "14px", sm: "14px" },
                                        px: { xs: 2, sm: 2 },
                                        py: { xs: 0.75, sm: 0.75 },
                                        minWidth: { xs: "80px", sm: "auto" },
                                        maxWidth: "100%",
                                    }}
                                >
                                    Confirm
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCancelSelectedArticles}
                                    disabled={selectedArticles.length === 0}
                                    sx={{
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        fontSize: { xs: "14px", sm: "14px" },
                                        px: { xs: 2, sm: 2 },
                                        py: { xs: 0.75, sm: 0.75 },
                                        minWidth: { xs: "80px", sm: "auto" },
                                        maxWidth: "100%",
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        )}
                        <Box
                            sx={{
                                mb: { xs: 1.5, sm: 1 },
                                maxWidth: "100%",
                                overflowX: "hidden",
                            }}
                        >
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Search article, code, standard, or damage..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" sx={{ fontSize: { xs: "20px", sm: "20px" } }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: "12px",
                                        backgroundColor: "#fff",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#e0e0e0",
                                        },
                                        height: { xs: "40px", sm: "40px" },
                                        fontSize: { xs: "16px", sm: "16px" },
                                        padding: { xs: "10px 14px", sm: "10px 14px" },
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                            <TableContainer
                                sx={{
                                    width: { xs: "calc(100vw - 130px)", sm: "100%" },
                                    maxWidth: { xs: "calc(100vw - 130px)", sm: "100%" },
                                    marginTop: { xs: "10px", sm: "10px" },
                                    maxHeight: { xs: "200px", sm: "200px", md: "200px" },
                                    overflowY: "auto",
                                    overflowX: { xs: "auto", sm: "hidden" },
                                    boxSizing: "border-box",
                                    "&::-webkit-scrollbar": {
                                        height: { xs: "6px", sm: "10px" },
                                        width: { xs: "6px", sm: "10px" },
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#888",
                                        borderRadius: "4px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#f1f1f1",
                                    },
                                    scrollbarWidth: { xs: "thin", sm: "auto" },
                                    scrollbarColor: { xs: "#888 #f1f1f1", sm: "auto" },
                                }}
                            >
                                <Table
                                    stickyHeader
                                    aria-label="article table"
                                    sx={{
                                        minWidth: { xs: "200x", sm: "450px" },
                                        width: "100%",
                                        maxWidth: "100%",
                                        tableLayout: "auto",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                "& > th": {
                                                    padding: { xs: "6px 3px", sm: "2px 8px", md: "2px 10px" },
                                                    fontWeight: "bold",
                                                    bgcolor: "#d5d8e7ff",
                                                    color: "#333",
                                                    fontSize: { xs: "15px", sm: "14px", md: "16px" },
                                                    border: "0.5px solid #ddd",
                                                },
                                            }}
                                        >
                                            {columns.map((column) => {
                                                const currentMinWidth =
                                                    typeof column.minWidth === "object"
                                                        ? column.minWidth.xs || column.minWidth.sm || column.minWidth
                                                        : column.minWidth;
                                                return (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.align}
                                                        style={{ minWidth: currentMinWidth }}
                                                        sx={{
                                                            padding: { xs: "6px 3px", sm: "8px" },
                                                            width: "auto",
                                                            boxSizing: "border-box",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            title={column.id.charAt(0).toUpperCase() + column.id.slice(1)}
                                                            sx={{
                                                                fontSize: { xs: "15px", sm: "14px", md: "16px" },
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {column.id.charAt(0).toUpperCase() + column.id.slice(1)}
                                                        </Typography>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredRows.length > 0 ? (
                                            filteredRows.map((row, idx) => (
                                                <TableRow
                                                    hover
                                                    role="checkbox"
                                                    tabIndex={-1}
                                                    key={row.id}
                                                    sx={{
                                                        "& > td": {
                                                            padding: { xs: "6px 3px", sm: "2px 6px", md: "2px 6px 2px 10px" },
                                                            border: "0.5px solid #ddd",
                                                            boxSizing: "border-box",
                                                        },
                                                        "&:hover": { backgroundColor: "#f1f1f1", cursor: "pointer" },
                                                        transition: "background-color 0.3s ease",
                                                    }}
                                                >
                                                    {columns.map((column) => {
                                                        if (column.id === "sno") {
                                                            return (
                                                                <TableCell
                                                                    key="sno"
                                                                    align={column.align}
                                                                    style={{ minWidth: column.minWidth.xs }}
                                                                    sx={{
                                                                        padding: { xs: "6px 3px", sm: "8px" },
                                                                        boxSizing: "border-box",
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: { xs: "15px", sm: "14px" },
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {idx + 1}
                                                                    </Typography>
                                                                </TableCell>
                                                            );
                                                        }
                                                        if (column.id === "actions") {
                                                            return (
                                                                <TableCell
                                                                    key="actions"
                                                                    align={column.align}
                                                                    style={{ minWidth: column.minWidth.xs }}
                                                                    sx={{
                                                                        padding: { xs: "6px 3px", sm: "8px" },
                                                                        display: "flex",
                                                                        flexWrap: "nowrap",
                                                                        gap: { xs: 0.5, sm: 1 },
                                                                        justifyContent: "center",
                                                                        overflow: "hidden",
                                                                        boxSizing: "border-box",
                                                                    }}
                                                                >
                                                                    {editingRow === idx ? (
                                                                        <>
                                                                            <Tooltip title="Save" arrow>
                                                                                <IconButton
                                                                                    onClick={() => handleSaveClick(row, idx)}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        padding: { xs: "4px", sm: "4px" },
                                                                                        minWidth: { xs: "28px", sm: "36px" },
                                                                                        "& svg": { fontSize: { xs: "18px", sm: "20px" } },
                                                                                    }}
                                                                                >
                                                                                    <TurnedInIcon sx={{ color: "#23740eff" }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Cancel" arrow>
                                                                                <IconButton
                                                                                    onClick={handleCancel}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        padding: { xs: "4px", sm: "4px" },
                                                                                        minWidth: { xs: "28px", sm: "36px" },
                                                                                        "& svg": { fontSize: { xs: "18px", sm: "20px" } },
                                                                                    }}
                                                                                >
                                                                                    <CancelIcon sx={{ color: "#d21919ff" }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Tooltip title="Edit" arrow>
                                                                                <IconButton
                                                                                    onClick={() => handleEditClick(row, idx)}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        padding: { xs: "4px", sm: "4px" },
                                                                                        minWidth: { xs: "28px", sm: "36px" },
                                                                                        "& svg": { fontSize: { xs: "18px", sm: "20px" } },
                                                                                    }}
                                                                                >
                                                                                    <EditIcon sx={{ color: "#1976d2" }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Delete" arrow>
                                                                                <IconButton
                                                                                    onClick={() => handleDeleteClick(idx)}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        padding: { xs: "4px", sm: "4px" },
                                                                                        minWidth: { xs: "28px", sm: "36px" },
                                                                                        "& svg": { fontSize: { xs: "18px", sm: "20px" } },
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon sx={{ color: "#d21919ff" }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </>
                                                                    )}
                                                                </TableCell>
                                                            );
                                                        }
                                                        const currentMinWidth =
                                                            typeof column.minWidth === "object"
                                                                ? column.minWidth.xs || column.minWidth.sm || column.minWidth
                                                                : column.minWidth;
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align={column.align}
                                                                style={{ minWidth: currentMinWidth }}
                                                                sx={{
                                                                    padding: { xs: "6px 3px", sm: "8px" },
                                                                    overflow: "hidden",
                                                                    whiteSpace: "nowrap",
                                                                    textOverflow: "ellipsis",
                                                                    boxSizing: "border-box",
                                                                }}
                                                            >
                                                                {column.editable && editingRow === idx ? (
                                                                    <TextField
                                                                        variant="outlined"
                                                                        size="small"
                                                                        value={editedValues[column.id] ?? row[column.id] ?? ""}
                                                                        onChange={(e) => handleInputChange(e, column.id)}
                                                                        inputProps={{
                                                                            style: {
                                                                                padding: { xs: "6px 8px", sm: "6px 8px" },
                                                                                fontSize: { xs: "15px", sm: "14px" },
                                                                            },
                                                                        }}
                                                                        sx={{
                                                                            width: "100%",
                                                                            maxWidth: "100%",
                                                                            overflowX: "hidden",
                                                                            "& .MuiOutlinedInput-root": {
                                                                                height: { xs: "36px", sm: "32px", md: "25px" },
                                                                            },
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: { xs: "15px", sm: "14px" },
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                        title={row[column.id]}
                                                                    >
                                                                        {row[column.id]}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={7}
                                                    align="center"
                                                    sx={{ py: 3, color: "#888", fontSize: { xs: "15px", sm: "14px" } }}
                                                >
                                                    No data available
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                                mt: { xs: 1.5, sm: 2 },
                                maxWidth: "100%",
                                overflowX: "hidden",
                            }}
                        >
                            <Tooltip title={selectedArticles.length > 0 ? "Please confirm or cancel article selections first" : ""} arrow>
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmit}
                                        disabled={selectedArticles.length > 0}
                                        sx={{
                                            borderRadius: "8px",
                                            textTransform: "none",
                                            fontSize: { xs: "14px", sm: "14px" },
                                            px: { xs: 2, sm: 2 },
                                            py: { xs: 0.75, sm: 0.75 },
                                            minWidth: { xs: "80px", sm: "auto" },
                                            maxWidth: "100%",
                                        }}
                                    >
                                        Submit
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleCancelNewRows}
                                disabled={rows.filter((row) => row.ArtConfigDtlId === null).length === 0}
                                sx={{
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontSize: { xs: "14px", sm: "14px" },
                                    px: { xs: 2, sm: 2 },
                                    py: { xs: 0.75, sm: 0.75 },
                                    minWidth: { xs: "80px", sm: "auto" },
                                    maxWidth: "100%",
                                }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Paper>
        </Box>
    );
};

export default ChkinArtConfig;